import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Button } from 'components/common';
import { FaTimes, FaPaperPlane } from 'react-icons/fa';
import { getPlanningEmailConfig } from 'redux/crm/releaseForecastSlice';
import ForecastEmailFields from '../ForecastEmailFields';
import { recipientGreeting } from '../components/helpers';

// Text-only, whitespace-insensitive compare — TinyMCE re-serialises whatever we
// hand it (tags reordered, entities rewritten), so only the words are reliable.
const squash = (html) => String(html || '').replace(/<[^>]*>/g, '').replace(/\s+|&nbsp;/g, '').toLowerCase();

const buildDefaultBody = ({ recipient, count, period }) =>
  `<p>Dear <strong>${recipient}</strong>,</p><p>Planning requests release forecast materials for <strong>${count} selected project(s)</strong> in <strong>${period}</strong>.</p><p>Please release the forecast-material documents of these buildings in the Portal — CSD is in copy to follow up. The Portal will update the completion status automatically.</p><p>Best Regards</p>`;

const MaterialRequestDialog = ({ period, count, onConfirm, onCancel }) => {
  const dispatch = useDispatch();
  const [toEmails, setToEmails] = useState([]);
  const [ccEmails, setCcEmails] = useState([]);
  const [subject, setSubject] = useState(`REQUEST RELEASE FORECAST MATERIALS - ${period}`);
  const [body, setBody] = useState('');
  const [bodyEdited, setBodyEdited] = useState(false);
  const generatedBody = useRef('');

  // Greets every recipient, e.g. "Dear TKP" or "Dear TKP/MIH".
  const recipient = useMemo(() => recipientGreeting(toEmails, 'ENG Team'), [toEmails]);
  const [configLoaded, setConfigLoaded] = useState(false);

  useEffect(() => {
    if (bodyEdited) return;
    const generated = buildDefaultBody({ recipient, count, period });
    generatedBody.current = generated;
    setBody(generated);
  }, [recipient, count, period, bodyEdited]);

  const handleBodyChange = (value) => {
    if (squash(value) !== squash(generatedBody.current)) setBodyEdited(true);
    setBody(value);
  };

  // Straight to ENG (who release the documents), CSD in Cc — App Settings →
  // CRM → Forecast: "Release Forecast ENG recipients" + the Sales recipients (CSD).
  useEffect(() => {
    dispatch(getPlanningEmailConfig({ target: 'eng' })).unwrap().then((config) => {
      setToEmails(config.to_emails || []);
      setCcEmails(config.cc_emails || []);
    }).catch(() => {}).finally(() => setConfigLoaded(true));
  }, [dispatch]);

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-p-4" style={{ zIndex: 999999 }}>
      <div className="tw-bg-white tw-rounded tw-shadow-2xl tw-w-full tw-max-w-2xl tw-max-h-[92vh] tw-overflow-y-auto">
        <div className="tw-flex tw-items-center tw-gap-3 tw-bg-blue-700 tw-text-white tw-px-5 tw-py-4">
          <FaPaperPlane />
          <div><h5 className="tw-font-bold tw-mb-0">Request forecast materials</h5><p className="tw-text-xs tw-text-blue-100 tw-mb-0">{count} selected project(s)</p></div>
          <button type="button" onClick={onCancel} className="tw-ml-auto tw-bg-transparent tw-border-0 tw-text-white"><FaTimes /></button>
        </div>
        <div className="tw-p-5">
          <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded tw-p-3 tw-text-xs tw-text-blue-800 tw-mb-4">
            Sent to ENG, with CSD in Cc. Status changes to Completed only after every selected building has a released forecast-material document.
          </div>
          {configLoaded && toEmails.length === 0 && (
            <div role="note" className="tw-bg-amber-50 tw-border tw-border-solid tw-border-amber-200 tw-rounded tw-p-3 tw-text-xs tw-text-amber-800 tw-mb-4">
              No ENG recipients are set yet — add them below, or ask an admin to fill App Settings → CRM → Forecast → &quot;Release Forecast ENG recipients&quot;.
            </div>
          )}
          <ForecastEmailFields
            toEmails={toEmails} ccEmails={ccEmails} subject={subject} body={body}
            onToChange={setToEmails} onCcChange={setCcEmails}
            onSubjectChange={setSubject} onBodyChange={handleBodyChange}
          />
          <div className="tw-flex tw-justify-end tw-gap-2 tw-mt-5">
            <Button variant="secondary" onClick={onCancel}>Cancel</Button>
            <Button disabled={!toEmails.length} onClick={() => onConfirm({ to_emails: toEmails, cc_emails: ccEmails, subject, body })}>
              <FaPaperPlane className="tw-inline tw-mr-1" /> Send request
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

MaterialRequestDialog.propTypes = { period: PropTypes.string, count: PropTypes.number, onConfirm: PropTypes.func, onCancel: PropTypes.func };
export default MaterialRequestDialog;
